import { useMutation, useQuery, useQueryClient } from 'react-query';

import { WebhookEvent } from '@bandido/shared/constants';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

export const WEBHOOK_EVENTS: readonly WebhookEvent[] = Object.values(WebhookEvent);

export type WebhookEventValue = WebhookEvent;

export interface WebhookConfig {
  id: string;
  agencyId: number;
  url: string;
  events: string[];
  workflowIds: string[] | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookFormValues {
  url: string;
  secret?: string;
  events: string[];
  workflowIds: string[];
  isActive: boolean;
}

export const useWebhooks = (agencyId: number) =>
  useQuery({
    queryKey: [QueryKeys.getWebhooks, agencyId],
    queryFn: async (): Promise<WebhookConfig[]> => {
      const response = await client.get<WebhookConfig[]>(`agencies/${agencyId}/webhooks`);
      return response.data;
    },
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: true,
  });

export const useSaveWebhook = (agencyId: number, webhookId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: WebhookFormValues) => {
      const basePayload = {
        url: values.url,
        events: values.events,
        workflowIds: values.workflowIds.length > 0 ? values.workflowIds : null,
        isActive: values.isActive,
      };

      const hasSecret = Boolean(values.secret && values.secret.trim() !== '');

      if (webhookId) {
        // On update, only send the secret when the user typed a new one,
        // so leaving the field blank preserves the existing secret.
        const payload: Record<string, unknown> = { ...basePayload };
        if (hasSecret) payload.secret = values.secret;
        await client.patch(`agencies/${agencyId}/webhooks/${webhookId}`, payload);
      } else {
        await client.post(`agencies/${agencyId}/webhooks`, {
          ...basePayload,
          ...(hasSecret ? { secret: values.secret } : {}),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getWebhooks]);
    },
  });
};

export const useDeleteWebhook = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.delete(`agencies/${agencyId}/webhooks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getWebhooks]);
    },
  });
};
